// DS3.8.1
import React from 'react';
import c from 'classnames';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InputLabelSubText from '@wix/da-ds/pkg/formControls/lego/InputLabelSubText';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import AttachIcon from '@wix/da-ds/pkg/Icons/16x16/Attach';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import { DropEvent } from 'react-dropzone';
import { useFileUpload } from '../utils/hooks/useFileUpload';
import { mimeTypeToPcpType } from '../utils/mime';
import {
  UploadFailureCallback,
  UploadSuccessCallback,
  AttachmentType,
} from '../redux/uploads/types';
import UploadedFile, { UploadedFileProps } from './UploadedFile';
import s from './FileUploadField.scss';

interface Props {
  id: string; // for a11y
  className?: string;
  uploadsListClassName?: string;
  label?: string;
  subLabel?: string;
  allowUpload?: boolean;
  maxItems?: number;
  uploadButtonInList?: boolean;
  uploadButtonLabel?: string;
  uploadButtonBiData?: any;
  items?: {
    name: string;
    deviationId: number;
    fileId?: number;
    size: File['size'];
    type: File['type'];
    previewUrl?: string;
    preview?: React.ReactNode;
    status?: UploadedFileProps['status'];
    rightSlot?: React.ReactElement;
  }[];
  parentDeviationId?: number /* needed for multi image & pcp */;
  isAttachment?: boolean;
  attachmentType?: AttachmentType;
  acceptedMimeTypes: string[];
  /** word wrap long names instead of truncating them? */
  wrapLongNames?: boolean;
  customFirstFileName?: string;
  onDrop?: (files: File[], ev: DropEvent) => boolean;
  onSuccess: UploadSuccessCallback;
  onFailure?: UploadFailureCallback;
  onRemove: (payload: { deviationId: number; fileId?: number }) => void;
  onSelect?: (payload: { deviationId: number; selected: boolean }) => void;
}

const FileUploadField: React.FC<Props> = ({
  id,
  items = [],
  maxItems = 100,
  label,
  subLabel,
  allowUpload = true,
  uploadButtonInList = false,
  uploadButtonLabel,
  uploadButtonBiData,
  className,
  uploadsListClassName,
  parentDeviationId,
  isAttachment,
  attachmentType,
  acceptedMimeTypes,
  wrapLongNames,
  customFirstFileName,
  onSuccess,
  onFailure,
  onRemove,
  onDrop,
  onSelect,
}) => {
  const { input, showFilePicker, activeUploads, cancelUpload } = useFileUpload({
    id,
    allowMultiple: false,
    parentDeviationId,
    isAttachment,
    attachmentType,
    acceptedMimeTypes,
    onSuccess,
    onFailure,
    onDrop,
  });
  const maxLimitReached = items.length + activeUploads.length >= maxItems;
  const allowMoreUploads = allowUpload && !maxLimitReached;

  const hasCustomName =
    customFirstFileName && (items.length > 1 || activeUploads.length > 0);

  return (
    <div className={c(s['root'], className)}>
      <FormElement
        inputId={id}
        label={label && <InputLabelText>{label}</InputLabelText>}
        subLabel={
          subLabel && (
            <InputLabelSubText className={s['sublabel']}>
              {subLabel}
            </InputLabelSubText>
          )
        }
        noBottomSpacing={true}
      >
        <div className={c(s['uploaded-files'], uploadsListClassName)}>
          {items.map(
            (
              {
                deviationId,
                fileId,
                name,
                size,
                type,
                previewUrl,
                preview,
                status,
                rightSlot,
              },
              index
            ) => (
              <UploadedFile
                key={fileId || deviationId}
                name={hasCustomName && index === 0 ? customFirstFileName : name}
                customName={hasCustomName && index === 0 ? true : false}
                size={size}
                type={type}
                previewUrl={previewUrl}
                preview={preview}
                rightSlot={rightSlot}
                status={status || 'complete'}
                onRemove={() => onRemove({ deviationId, fileId })}
                wrapLongNames={wrapLongNames}
              />
            )
          )}
          {activeUploads.map(item => (
            <UploadedFile
              key={item.uploadId}
              name={item.file.name}
              size={item.file.size}
              type={mimeTypeToPcpType(item.file.type)}
              uploadProgress={item.progress}
              status={item.error ? 'error' : 'loading'}
              errorMessage={item.error ? item.error.message : undefined}
              onRemove={() => cancelUpload(item.uploadId)}
              wrapLongNames={wrapLongNames}
            />
          ))}
          {allowMoreUploads && uploadButtonInList && (
            <TextButton
              size="medium"
              variant="main"
              className={c(s['upload-button-updated'], 'ds-card-small')}
              tabIndex={0}
              onClick={showFilePicker}
              biData={uploadButtonBiData}
            >
              {uploadButtonLabel ? (
                <>
                  <AddIcon
                    className={s['upload-button-icon']}
                    disableDefaultColors
                  />
                  <span className={s['upload-button-content']}>
                    {uploadButtonLabel}
                  </span>
                </>
              ) : (
                <AddIcon className={s['upload-button-icon']} />
              )}
            </TextButton>
          )}
        </div>
      </FormElement>
      {allowMoreUploads && !uploadButtonInList && (
        <TextButton
          size="small"
          variant="main"
          className={s['upload-button']}
          tabIndex={0}
          icon={AttachIcon}
          onClick={showFilePicker}
          biData={uploadButtonBiData}
        >
          {uploadButtonLabel}
        </TextButton>
      )}
      {allowMoreUploads && input}
    </div>
  );
};
FileUploadField.displayName = 'FileUploadField';

export default FileUploadField;
